import React, { useEffect, createContext, useState, useCallback, useMemo } from 'react';
import { UserSection, GroupSection } from 'components/main/userGroup';
import { IStyle } from 'Styles';
import Header from 'components/header/Header';
import useRockFetchGet from 'utils/RockFetch';
import { GroupDTO, UserDTO, UserTypeDTO } from 'business/models/Models';
import useModel from 'business/models/useModel';
import UserGroupService from 'business/services/UserGroupService';
import {
  getCurrentUser,
  showAlertSnackBar,
  isAdministrator as getIsAdministrator,
  validateEmail,
  MaxNameLength,
  navigationBlockWarningMessage,
} from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { cloneDeep } from 'lodash';
import { SettingsTabsContext } from '../Settings/SettingsTabs';
import { RowExtraInfo, RowInfoType } from 'components/customComponents/Controls/RMTable';
import { KeyboardKeys } from 'constants/types';
import { useEventListener } from 'CustomHooks/useEventListener';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

export interface userGroupStateType {
  users: UserDTO[];
  groups: GroupDTO[];
  userTypes: UserTypeDTO[];
  isFetching?: boolean;
}

const userGroupDefaultState: userGroupStateType = {
  users: null,
  groups: null,
  userTypes: null,
  isFetching: false,
};
export const UserGroupContext = createContext(null);

const userService = new UserGroupService();

const ERROR_NAME_EMAIL_DUPLICATE = 'Name or email address already exists.\n';

const UserSetup = () => {
  const [fetchData] = useRockFetchGet();
  const [state, setState] = useState(userGroupDefaultState);
  const [isAdministrator, setIsAdministrator] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserDTO>(null);
  const [tempState, setTempState] = useState(userGroupDefaultState);
  const [rowError, setRowError] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [editIdUser, setEditIdUser] = useState<number>(0);
  const [editIdGroup, setEditIdGroup] = useState<number>(0);
  const [persistUserGroupDetails] = useModel(UserGroupService, 'saveUserGroupDetails');
  const { setHeaders } = React.useContext(SettingsTabsContext);
  const _fetchUserGroup = useCallback(
    (shouldUpdateLoggedInUser = false, shouldSelectLastItems = false) => {
      setTempState((prv) => ({ ...prv, isFetching: true }));
      setState((prv) => ({ ...prv, isFetching: true }));
      Promise.all([
        fetchData('api/user', null, null, true),
        fetchData('api/group?withUsers=true', null, null, true),
        userService.getUserTypes(),
      ]).then(([users, groups, userTypes]) => {
        setTempState((prv) => ({ ...prv, users, groups, userTypes, isFetching: false }));
        setState((prv) => ({
          ...prv,
          users: cloneDeep(users),
          groups: cloneDeep(groups),
          userTypes: cloneDeep(userTypes),
          isFetching: false,
        }));
        if (shouldUpdateLoggedInUser) {
          const user = users.find((u) => u.id === currentUser.id);
          if (!user) {
            return;
          }
          localStorage.setItem('user', JSON.stringify(user));
        }
        if (shouldSelectLastItems) {
          const wasNewGroupAdded = tempState.groups.some((g) => g.id < 0); // tempState is of previous cycle, with old state, not the the one above
          const wasNewUserAdded = tempState.users.some((u) => u.id < 0); // tempState is of previous cycle, with old state, not the the one above
          if (wasNewUserAdded) {
            setSelectedUsers([users[users.length - 1]]);
          }
          if (wasNewGroupAdded) {
            setSelectedGroups([groups[groups.length - 1]]);
          }
        }
      });
    },
    [currentUser, tempState],
  );

  const keyDownHandler = (e: KeyboardEvent) => {
    const { key } = e;
    const ctrlKey = e.ctrlKey || e.metaKey;
    if (ctrlKey && key === KeyboardKeys.S) {
      e.preventDefault();
      if (isDirty || !disableModificationButtons) {
        handleOnSaveClick();
      }
    }
  };
  useEventListener('keydown', keyDownHandler);

  const disableModificationButtons = useMemo(() => {
    try {
      return JSON.stringify(state) === JSON.stringify(tempState);
    } catch (ex: any) {
      console.error(ex);
      return true;
    }
  }, [state, tempState]);

  const handleOnSaveClick = useCallback(async () => {
    const rowInfo: RowExtraInfo = {};
    const { users, groups } = tempState;
    const { users: originalUsers } = state;

    users.forEach((usr, i) => {
      let msg = '';

      const existingUser = originalUsers.find((u) => u.id === usr.id);
      const userEdited =
        existingUser &&
        (usr.name !== existingUser.name || usr.email !== existingUser.email);

      const isDuplicateName = users.some((u) => u.id !== usr.id && u.name === usr.name);
      const isDuplicateEmail = users.some(
        (u) => u.id !== usr.id && u.email === usr.email,
      );

      if (!usr.name) {
        msg = 'Please Enter Name.\n';
      } else if (usr.name.length > MaxNameLength.USERS) {
        msg = `Please Enter A Name No Longer Than ${MaxNameLength.USERS} Characters`;
      } else if (!validateEmail(usr.email)) {
        msg = 'Please Enter Valid Email address.\n';
      } else if (isDuplicateName && isDuplicateEmail) {
        msg = ERROR_NAME_EMAIL_DUPLICATE;
      } else if (isDuplicateName) {
        msg = ERROR_NAME_EMAIL_DUPLICATE;
      } else if (userEdited && isDuplicateEmail) {
        // HACK: email check is for edited user only.
        // Non-dirty users will not have email validation
        msg = ERROR_NAME_EMAIL_DUPLICATE;
      } else if (!existingUser && (isDuplicateName || isDuplicateEmail)) {
        msg = ERROR_NAME_EMAIL_DUPLICATE;
      }

      if (msg) {
        rowInfo['users'] = rowInfo['users'] || {};
        rowInfo['users'][i] = { msg, type: RowInfoType.Error };
      }
    });
    groups.forEach((grp, i) => {
      let msg = '';
      if (!grp.name) {
        msg = 'Please Enter Name.\n';
      }
      if (tempState?.groups?.find((g) => g.id !== grp.id && g.name === grp.name)) {
        msg = 'Name already in use\n';
      }
      if (msg) {
        rowInfo['groups'] = rowInfo['groups'] || {};
        rowInfo['groups'][i] = { msg, type: RowInfoType.Error };
      }
    });
    if (Object.keys(rowInfo).length) {
      setRowError(rowInfo);
    } else {
      setRowError({});
    }
    if (!Object.keys(rowInfo).length) {
      try {
        await persistUserGroupDetails(tempState);
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          'Your changes have been saved',
        );
      } catch (exception: any) {
        showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, exception.message);
      }
      _fetchUserGroup(true, true);
      setIsDirty(false);
    }
  }, [persistUserGroupDetails, tempState, _fetchUserGroup]);
  const handleDiscard = useCallback(() => {
    setTempState(JSON.parse(JSON.stringify(state)));
    setIsDirty(false);
    setRowError({});
    setEditIdUser(0);
    setEditIdGroup(0);
  }, [setTempState, state]);

  /**
   * TODO: This is temporary. We need to Make proper data fetching hooks
   * and have thunks(if redux used) should fetch data from api(class).
   */
  useEffect(() => {
    const user: UserDTO = getCurrentUser();
    //TODO: put admins or group names in constants file
    const _isAdmin = getIsAdministrator();
    setIsAdministrator(_isAdmin);
    if (!_isAdmin) {
      return;
    }
    setCurrentUser(user);
    _fetchUserGroup();
  }, []);
  useEffect(() => {
    if (setHeaders) {
      setHeaders(
        <Header
          containerStyle={styles.header}
          headerButtons={isAdministrator ? saveDiscardButtons : null}
          title={'Administrator'}
          isNewTheme={true}
        />,
      );
    }
  }, [disableModificationButtons, tempState]);
  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: isDirty || !disableModificationButtons,
    onSave: handleOnSaveClick,
    onDiscard: handleDiscard,
    alwaysEnableDiscard: !!Object.keys(rowError).length,
    navigationBlockCallback: () => {
      if (isDirty) {
        return navigationBlockWarningMessage;
      }
    },
  });
  return (
    <>
      {/**
       * TODO: make this generic and use it in licensetab as well
       */}

      {isAdministrator ? (
        <div id='usersetup-wrapper' style={styles.root}>
          <UserGroupContext.Provider
            value={{
              state,
              setState,
              tempState,
              setTempState,
              rowError,
              setRowError,
              setIsDirty,
              fetchUserGroup: _fetchUserGroup,
            }}
          >
            {!state.isFetching && !state.users && !state.groups ? (
              <div id='usersetup-loading-message'>loading....</div>
            ) : (
              <div id='usersetup-content' className="flex flex-col h-[87vh]">
                <div id='usersetup-usersection-wrapper' className="h-1/2">
                  <UserSection
                    setSelectedUsers={setSelectedUsers}
                    selectedUsers={selectedUsers}
                    setEditId={setEditIdUser}
                    editId={editIdUser}
                  />
                </div>
                <div id='usersetup-groupsection-wrapper' className="h-1/2">
                  <GroupSection
                    setSelectedGroups={setSelectedGroups}
                    selectedGroups={selectedGroups}
                    setEditId={setEditIdGroup}
                    editId={editIdGroup}
                  />
                </div>
              </div>
            )}
          </UserGroupContext.Provider>
        </div>
      ) : (
        <div id='usersetup-access-denied-message'>{`You don't have access to this section.`}</div>
      )}
    </>
  );
};

const styles: IStyle = {
  root: {
    width: '100%',
    padding: 10,
    display: 'flex',
    gap: 10,
    flexDirection: 'column',
    backgroundColor: 'white',
    borderTop: '1px solid #9E9E9E',
    height: '100%',
  },
  header: {
    marginTop: -5,
  },
};
export default UserSetup;
